import { createSlice } from '@reduxjs/toolkit';
import {
  REPORTS_DASH,
  GET_REPORT_ATHENA,
  GET_REPORT_ATHENA_ERROR,
  RESET_REPORT_ATHENA_COUNT,
  GET_REPORT_ATHENA_CHANGE_REPORTID,
  GET_CUSTOMFILTEROPTIONS,
  EXPORT_RECOMMENDATION_REPORT,
  EXPORT_ADMINREVIEW_REPORT,
  RESET_REPORTSALERTS,
  RESET_REPORTSFILTERALERTS,
  EXPORT_REPORT_MESSAGE,
  FETCH_REPORTS_EXPORTS,
  REPORTS_EXPORTS_DOWNLOAD_START,
  REPORTS_EXPORTS_DOWNLOAD_STOP,
  REPORTS_EXPORTS_DOWNLOAD_ERROR,
  EXPORTS_SAVE_TASKCOMPLETE,
  REPORT_CONFLICT,
} from '../../../actions-index';
import { ERROR_CONSTS, STATUS } from '../../../util/constants';

// ===========================================================================
// reportsDashReducer slice
// ===========================================================================
const reportsDashSlice = createSlice({
  name: 'reportsDashReducer',
  initialState: {
    customReports: [],
    lastUpdatedTime: '',
    status: STATUS.LOADING,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(REPORTS_DASH, (state, action) => {
      state.status = action.payload.status ?? '';
      state.customReports = action.payload.data?.reports ?? [];
      state.lastUpdatedTime = action.payload.data?.lastUpdatedTime ?? '';
    });
  },
});

export const reportsDashReducer = reportsDashSlice.reducer;

// ===========================================================================
// athenaReportReducer slice
// ===========================================================================

// --- Helper functions ported from legacy reducer ---

function getNextToken(state, action) {
  return action.payload?.data?.nextToken ?? undefined;
}

function getQueryExecutionId(state, action) {
  return action.payload?.data?.queryExecutionId ?? undefined;
}

function getReportName(state, action) {
  return action.payload?.data?.reportName ?? state.reportName;
}

function cloneDeep(o) {
  return JSON.parse(JSON.stringify(o));
}

function getIdFromKeys(row, keys) {
  return keys.map((key) => row?.[key]).join('_');
}

function expandColumnsFromDataAsJson(row) {
  try {
    if (row.id) {
      return;
    }
    const keys = this;
    row.id = getIdFromKeys(row, keys);

    const dataAsJsonMap = row.dataAsJsonMap;
    delete row.dataAsJsonMap;

    if (typeof dataAsJsonMap !== 'string') {
      return;
    }

    const extraDataObj = JSON.parse(dataAsJsonMap);

    for (let key of Object.keys(extraDataObj)) {
      row[key] = extraDataObj[key];
    }

    row.id = getIdFromKeys(row, keys); // Re-do, now that we have more data
  } catch (exception) {
    console.error('Error while pulling dataAsJson', exception);
  }
}

function getKeys(columnDefinitions) {
  const defaultKeys = ['applicantemployeeid', 'committeeid', 'term'];

  if (!Array.isArray(columnDefinitions)) {
    return defaultKeys;
  }

  const keys = columnDefinitions
    .filter((column) => column?.isKey && typeof column.field === 'string')
    .map((column) => column.field);
  return keys.length > 0 ? keys : defaultKeys;
}

function getUnwrappedColumnName(headerName) {
  if (!headerName.includes('.')) {
    return headerName;
  }

  if (headerName.includes('."')) {
    headerName = headerName.split('."')[1];
    return headerName.slice(0, -1);
  }

  return headerName.split('.')[1];
}

function getSafeHeaderName(metadata = {}, key) {
  let headerName =
    (metadata.headerName?.length > 0 ? metadata.headerName : key) ?? key;
  return getUnwrappedColumnName(headerName);
}

function getColumnDefinitions(action) {
  const applications = action.payload?.data?.applications ?? [];
  let defaultColumnDefinitions = Object.keys(applications[0] ?? {})
    .map((columnKey) => ({
      field: columnKey,
      headerName: columnKey,
      width: '250',
    }))
    .filter((column) => column.field !== 'id');

  if (typeof action.payload?.data?.columnMappingsAsJson !== 'string') {
    return defaultColumnDefinitions;
  }

  try {
    const mappings = JSON.parse(action.payload?.data?.columnMappingsAsJson);
    let orderedColumnDefs = Object.entries(mappings).map((entry) => {
      return {
        field: getUnwrappedColumnName(entry[0]),
        headerName: getSafeHeaderName(entry[1], entry[0]),
        width: entry[1]?.width ?? '250',
        isKey: entry[1]?.isKey ?? false,
        isHidden: entry[1]?.isHidden ?? false,
      };
    });

    return orderedColumnDefs;
  } catch (exception) {
    console.error(
      `Error while parsing columnMappings=[${action.payload?.data?.columnMappingsAsJson}]`,
      exception
    );
  }

  return defaultColumnDefinitions;
}

function getApplications(state, action, isLoading, columnDefinitions) {
  if (isLoading) {
    return state.applications;
  }

  let applications = [];

  if (state.isGettingNextPage) {
    applications.push(...cloneDeep(state.applications ?? []));
  }

  if (action.payload?.data?.applications) {
    applications.push(...cloneDeep(action.payload.data.applications));
  }

  const columnKeys = getKeys(columnDefinitions);
  applications.forEach(expandColumnsFromDataAsJson.bind(columnKeys));

  return applications;
}

function getFilterOptions(state, action, isLoading) {
  if (isLoading && state.filterOptions) {
    return state.filterOptions;
  }
  const filtersAsJson = action.payload?.data?.filtersAsJson;

  try {
    if (typeof filtersAsJson === 'string') {
      const allFilters = JSON.parse(filtersAsJson);
      const filtersWithoutPrefilters = allFilters.filter(
        (filter) => filter.isPrefilter !== true
      );
      return filtersWithoutPrefilters;
    }
  } catch (exception) {
    console.error(
      `Error while parsing filter options for ${filtersAsJson}`,
      exception
    );
  }

  return [];
}

function getCount(state, action) {
  const { isGettingNextPage } = action?.payload ?? {};
  if (isGettingNextPage) {
    return state?.count ?? 0;
  }

  return isNaN(action?.payload?.data?.count)
    ? 0
    : parseFloat(action?.payload?.data?.count) ?? state?.count ?? 0;
}

const athenaReportInitialState = {
  type: GET_REPORT_ATHENA,
  applications: [],
  columns: [],
  count: 0,
  filterOptions: [],
  isLoading: true,
  queryExecutionId: [],
  nextToken: [],
  status: STATUS.LOADING,
  errorCount: 0,
};

const athenaReportSlice = createSlice({
  name: 'athenaReportReducer',
  initialState: athenaReportInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(GET_REPORT_ATHENA, (state, action) => {
        const isLoading = action?.payload?.status === STATUS.LOADING;

        // Handle when the user clears an error popup gracefully
        if (
          action?.payload?.status === null &&
          action.payload.alert?.MESSAGE === '' &&
          action.payload.alert.SEVERITY === null &&
          action.payload.details === ''
        ) {
          state.status = action.payload.status ?? '';
          state.alert =
            action.payload.alert ?? ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT;
          return;
        }

        const columnDefinitions = getColumnDefinitions(action);

        // Must return a new object since many fields are computed from helpers
        // that reference the previous state
        return {
          type: GET_REPORT_ATHENA,
          status: action.payload.status ?? '',
          isGettingNextPage:
            action.payload.isGettingNextPage ?? state.isGettingNextPage,
          isLoading,
          alert: action.payload.alert ?? ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT,
          applications: getApplications(
            state,
            action,
            isLoading,
            columnDefinitions
          ),
          columns: columnDefinitions,
          count: getCount(state, action),
          filterOptions: getFilterOptions(state, action, isLoading),
          queryExecutionId: getQueryExecutionId(state, action),
          nextToken: getNextToken(state, action),
          reportName: getReportName(state, action),
          errorCount: !isLoading ? 0 : state.errorCount ?? 0,
        };
      })
      .addCase(GET_REPORT_ATHENA_ERROR, (state, action) => {
        const isLoading = action?.payload?.status === STATUS.LOADING;
        state.type = GET_REPORT_ATHENA;
        state.status = action.payload.status ?? '';
        state.isGettingNextPage =
          action.payload.isGettingNextPage ?? state.isGettingNextPage;
        state.isLoading = isLoading;
        state.alert =
          action.payload.alert ?? ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT;
        state.errorCount = (state.errorCount ?? 0) + 1;
      })
      .addCase(RESET_REPORT_ATHENA_COUNT, (state) => {
        state.count = 0;
      })
      .addCase(GET_REPORT_ATHENA_CHANGE_REPORTID, (state) => {
        return { ...athenaReportInitialState, reportName: state.reportName };
      });
  },
});

export const athenaReportReducer = athenaReportSlice.reducer;

// ===========================================================================
// reportsReducer slice
// ===========================================================================

function getSafeCustomFilters(str) {
  if (!str) {
    return;
  }

  try {
    return JSON.parse(str);
  } catch (exception) {
    console.error(
      'Error while parsing custom filters from str',
      str,
      exception
    );
  }

  return [];
}

const reportsSlice = createSlice({
  name: 'reportsReducer',
  initialState: {
    report: {
      status: STATUS.LOADING,
      alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT,
      data: {
        rows: [],
        filterOptionsOriginal: [],
        filterOptionsCustom: [],
      },
    },
    exportStatus: {
      status: '',
      alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT,
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(GET_CUSTOMFILTEROPTIONS, (state, action) => {
        const filterOptionsCustom = getSafeCustomFilters(action.payload.data);

        state.report.data.filterOptions = [
          ...(state.report.data.filterOptionsOriginal ?? []),
          ...(filterOptionsCustom ?? []),
        ];
        state.report.data.filterOptionsCustom =
          filterOptionsCustom ?? state.report.data.filterOptionsCustom ?? [];
        state.report.data.filterOptionsCustomStatus = action.payload.status;
        state.report.data.isLoadingCustomFilters =
          action.payload.status === STATUS.LOADING;
        state.report.data.customFiltersErrorAlert = action.payload.alert;
      })
      .addCase(RESET_REPORTSALERTS, (state, action) => {
        let resetReportAlerts = action.payload?.status !== STATUS.ERROR;
        if (resetReportAlerts) {
          state.report.status = action.payload.status;
          state.report.alert = action.payload.alert;
        }
      })
      .addCase(RESET_REPORTSFILTERALERTS, (state, action) => {
        let resetReportAlerts = action.payload?.status !== STATUS.ERROR;
        if (resetReportAlerts) {
          state.report.data.filterOptionsCustomStatus =
            state?.data?.isLoadingCustomFilters
              ? state?.data?.filterOptionsCustomStatus
              : STATUS.SUCCESS;
          state.report.data.customFiltersErrorAlert = undefined;
        }
      })
      .addCase(EXPORT_RECOMMENDATION_REPORT, (state, action) => {
        state.exportStatus = {
          ...reportsInitialState,
          status: action.payload.status,
          alert: action.payload.alert,
          type: EXPORT_RECOMMENDATION_REPORT,
        };
      })
      .addCase(EXPORT_ADMINREVIEW_REPORT, (state, action) => {
        state.exportAdminReviewsStatus = {
          ...reportsInitialState,
          status: action.payload.status,
          alert: action.payload.alert,
          type: EXPORT_ADMINREVIEW_REPORT,
        };
      })
      .addCase(EXPORT_REPORT_MESSAGE, (state, action) => {
        state.exportReportMessage = {
          status: action.status ?? STATUS.SUCCESS,
          alert: {
            MESSAGE: action.message,
          },
          downloadComplete: action.downloadComplete ?? true,
        };
      });
  },
});

export const reportsReducer = reportsSlice.reducer;

// ===========================================================================
// reportsExportsReducer slice
// ===========================================================================

function getUpdatedTaskData(payload, exports) {
  const { executionId, recommendationKey, saving, error, isComplete } = payload;

  let exportsCopy = JSON.parse(JSON.stringify(exports));
  let currentExport = exportsCopy.find(
    (currentExport) => currentExport.executionId === executionId
  );
  let currentRecommendationLink = currentExport.recommendations?.find?.(
    (recommendation) => recommendation.recommendation === recommendationKey
  );

  if (saving) {
    currentRecommendationLink.isSaving = saving;
    currentRecommendationLink.isError = null;
  } else {
    currentRecommendationLink.isSaving = false;
    currentRecommendationLink.isError = error ? true : false;
    currentRecommendationLink.error = error;
    if (typeof isComplete !== 'undefined') {
      currentRecommendationLink.isComplete = isComplete;
    }
  }

  let taskErrorMessages = exportsCopy.reduce((errorArray, currentExport) => {
    currentExport.recommendations?.forEach((recommendation) => {
      if (recommendation.isError) {
        errorArray.push(JSON.stringify(recommendation.error));
      }
    });

    return errorArray;
  }, []);

  return { data: exportsCopy, taskErrorMessages };
}

const reportsExportsSlice = createSlice({
  name: 'reportsExportsReducer',
  initialState: {
    status: STATUS.LOADING,
    alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT,
    data: [],
    isGeneratingDownload: false,
    type: FETCH_REPORTS_EXPORTS,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(FETCH_REPORTS_EXPORTS, (state, action) => {
        state.status = action.payload.status ?? STATUS.LOADING;
        state.downloadErrorMessage =
          action.payload.alert?.MESSAGE ??
          ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT?.MESSAGE;
        state.data = action.payload.data ?? [];
      })
      .addCase(REPORTS_EXPORTS_DOWNLOAD_START, (state) => {
        state.isGeneratingDownload = true;
      })
      .addCase(REPORTS_EXPORTS_DOWNLOAD_STOP, (state) => {
        state.isGeneratingDownload = false;
      })
      .addCase(REPORTS_EXPORTS_DOWNLOAD_ERROR, (state, action) => {
        state.downloadErrorMessage = action.payload ?? null;
        state.isGeneratingDownload = false;
      })
      .addCase(EXPORTS_SAVE_TASKCOMPLETE, (state, action) => {
        const updated = getUpdatedTaskData(action.payload, state.data);
        state.data = updated.data;
        state.taskErrorMessages = updated.taskErrorMessages;
      });
  },
});

export const reportsExportsReducer = reportsExportsSlice.reducer;

// ===========================================================================
// reportConflictReducer slice
// ===========================================================================

const reportConflictSlice = createSlice({
  name: 'reportConflictReducer',
  initialState: {
    reportConflict: {
      status: '',
      alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT,
      type: REPORT_CONFLICT,
    },
  },
  reducers: {
    reportConflict(state, action) {
      state.reportConflict.status = action.payload.status;
      state.reportConflict.alert = action.payload.alert;
      state.reportConflict.type = REPORT_CONFLICT;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REPORT_CONFLICT, (state, action) => {
      state.reportConflict.status = action.payload.status;
      state.reportConflict.alert = action.payload.alert;
      state.reportConflict.type = REPORT_CONFLICT;
    });
  },
});

export const { reportConflict } = reportConflictSlice.actions;
export const reportConflictReducer = reportConflictSlice.reducer;
